import "./App.css";
import { useState, useEffect, Suspense, lazy } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import firebase from "./firebase";
import i18n from "./i18n";
import { isAeroDomain } from "./utils/domainUtils";
import {
  DashboardSkeleton,
  FormSkeleton,
  BillingSkeleton,
  PageSkeleton,
  UserTestingSkeleton,
} from "./components/Skeletons/Skeletons";
// Aero core pages — regular imports so they are always bundled, never triggering
// Suspense on navigation between User Testing, Responsive, and Iterate.
import AeroLayout from "./layouts/AeroLayout.js";
import UserTestingLayout from "./pages/UserTesting/UserTestingLayout.js";
import UserTestingDashboard from "./pages/UserTesting/UserTestingDashboard.js";
import AeroHome from "./pages/AeroHome/AeroHome.js";
import AeroTestNew from "./pages/UserTesting/AeroTestNew.js";
import ResponsiveDashboard from "./pages/ResponsiveDashboard/ResponsiveDashboard.js";
import IterateDashboard from "./pages/IterateDashboard/IterateDashboard.js";
import AeroSites from "./pages/AeroSites/AeroSites.js";
import AeroSiteDetail from "./pages/AeroSites/AeroSiteDetail.js";
import AeroSiteNew from "./pages/AeroSites/AeroSiteNew.js";
import AeroUsers from "./pages/AeroUsers/AeroUsers.js";
import AeroSiteVersion from "./pages/AeroSites/AeroSiteVersion.js";
// import 'firebase/analytics';
// import { getAnalytics, logEvent } from "firebase/analytics";

// Lazy load components

const DynamicPage = lazy(() => import("./pages/DynamicPage/DynamicPage"));
const DynamicPage2 = lazy(() => import("./pages/DynamicPage2.js"));
const LandingPage = lazy(() => import("./pages/LandingPage/LandingPage"));
const UserDashboard = lazy(() => import("./pages/UserDashboard/UserDashboard"));
const ForgotPassword = lazy(
  () => import("./pages/Authentication/ForgotPassword/ForgotpasswordPage")
);
const AeroForgotPassword = lazy(
  () => import("./pages/Authentication/ForgotPassword/AeroForgotPasswordPage")
);
const AuthActionPage = lazy(
  () => import("./pages/Authentication/AuthAction/AuthActionPage")
);
const Preview = lazy(() => import("./pages/Preview/Preview.js"));
const Mainauth = lazy(() => import("./pages/Authentication/MainAuth"));
const BillingPage = lazy(() => import("./pages/BillingPage/Billing.js"));
const ProfilePage = lazy(() => import("./pages/ProfilePage/ProfilePage"));
const AdminDashboard = lazy(
  () => import("./pages/AdminDashboard/AdminDashboard.js")
);
const AbPreview = lazy(() => import("./pages/AbPreview/AbPreview.js"));
// Aero-side endpoint of the "land already logged-in" handoff (one-time code ->
// custom token). Served on the Aero app origin (app.aeroapp.ai/redeem).
const RedeemHandoff = lazy(() => import("./pages/RedeemHandoff/RedeemHandoff.js"));
const TermsAndConditions = lazy(
  () => import("./pages/TermsAndConditions/TermsAndConditions.js")
);
const PrivacyPolicy = lazy(
  () => import("./pages/Privacy Policy/PrivacyPolicy.js")
);
const FolioForm = lazy(() => import("./pages/FolioForm/FolioForm.js"));
const MobileFormInstruction = lazy(
  () =>
    import("./pages/MobileForm/MobileInstructionForm/MobileFormInstruction.js")
);
const MobileFormTitle = lazy(
  () => import("./pages/MobileForm/MobileFormTitle/MobileFormTitle.js")
);
const MobileFormContent = lazy(
  () => import("./pages/MobileForm/MobileFormContent/MobileFormContent.js")
);
const MobileFormDomain = lazy(
  () => import("./pages/MobileForm/MobileFormDomain/MobileFormDomain.js")
);
const MobileFormFavicon = lazy(
  () => import("./pages/MobileForm/MobileFormFavicon/MobileFormFavicon.js")
);
const MobileFormPassword = lazy(
  () => import("./pages/MobileForm/MobileFormPassword/MobileFormPassword.js")
);
const MobileFormLabel = lazy(
  () => import("./pages/MobileForm/MobileFormLabel/MobileFormLabel.js")
);
const MobileAnalytics = lazy(
  () => import("./pages/MobileForm/MobileAnalytics/MobileAnalytics.js")
);
const MobileUserTesting = lazy(
  () => import("./pages/MobileForm/MobileUserTesting/MobileUserTesting.js")
);
const PreviewFromPlugin = lazy(
  () => import("./pages/Preview/PreviewFromPlugin.js")
);
const PluginAuth = lazy(() => import("./pages/Authentication/PluginAuth.js"));
const UTPluginAuth = lazy(() => import("./pages/Authentication/UTPluginAuth.js"));
const ResponsiveAuth = lazy(() => import("./pages/Authentication/ResponsiveAuth.js"));
const TestHtml = lazy(() => import("./pages/TestHtml.js"));
const PricingTest = lazy(() => import("./pages/PricingTest/PricingTest.js"));
const SessionVideoPlayer = lazy(() => import("./pages/SessionVideoPlayer/SessionVideoPlayer.js"));

// User Testing Demo and Dashboard
const DemoUserTesting = lazy(() => import("./pages/DemoUserTesting/DemoUserTesting.js"));
const UserTestingProject = lazy(() => import("./pages/UserTesting/UserTestingProject.js"));
const UTSessionsTab = lazy(() => import("./pages/UserTesting/tabs/UTSessionsTab.js"));
const AeroNotFoundPage = lazy(() => import("./components/Aero/AeroNotFoundPage"));
const UTSessionDetailPage = lazy(() => import("./pages/UserTesting/tabs/UTSessionDetailPage.js"));
const AdminSessionReview = lazy(() => import("./pages/AeroUsers/AdminSessionReview.js"));
const UTInsightsTab = lazy(() => import("./pages/UserTesting/tabs/UTInsightsTab.js"));
const UTSetupTab = lazy(() => import("./pages/UserTesting/tabs/UTSetupTab.js"));
const UTProjectIndexRedirect = lazy(() => import("./pages/UserTesting/UTProjectIndexRedirect.js"));

// Account page
const Account = lazy(() => import("./pages/Account/Account.js"));

// Aero Component Showcase
const AeroShowcase = lazy(() => import("./pages/AeroShowcase/AeroShowcase.js"));

// Aero Legal Pages
const AeroPrivacyPolicy = lazy(() => import("./pages/AeroPrivacyPolicy/AeroPrivacyPolicy.js"));
const AeroTermsOfService = lazy(() => import("./pages/AeroTermsOfService/AeroTermsOfService.js"));

// User Testing Preview (no auth required)
const UTPreviewTest = lazy(() => import("./pages/UTPreview/UTPreviewTest.js"));
const UTPreviewResults = lazy(() => import("./pages/UTPreview/UTPreviewResults.js"));
const PreviewSessionVideoPlayer = lazy(() => import("./pages/UTPreview/PreviewSessionVideoPlayer.js"));

// Smart fallback that shows appropriate skeleton based on current route
function SuspenseFallback() {
  const location = useLocation();
  const path = location.pathname;

  // Landing page - no skeleton needed, loads fast
  // On Aero domain, root shows User Testing (needs skeleton like other Aero routes)
  if (path === "/" || path.match(/^\/[a-z]{2}\/?$/)) {
    if (isAeroDomain()) return <UserTestingSkeleton />;
    return null;
  }

  // Aero product routes — use the Aero skeleton (sidebar + content shape)
  if (
    path.includes("/user-testing") ||
    path.includes("/ut-test") ||
    path.includes("/ut-results") ||
    path.includes("/iterate") ||
    path.includes("/responsive")
  ) {
    return <UserTestingSkeleton />;
  }

  // Dashboard routes
  if (path.includes("/dashboard") || path.includes("/admin")) {
    return <DashboardSkeleton />;
  }

  // Demo routes - no skeleton
  if (path.includes("/demo/")) {
    return null;
  }

  // Form routes
  if (
    path.includes("/folio-form") ||
    path.includes("/mobile-form") ||
    path.includes("/mobile-instruction")
  ) {
    return <FormSkeleton />;
  }

  // Billing and profile routes
  if (path.includes("/billing") || path.includes("/profile")) {
    return <BillingSkeleton />;
  }

  // Auth/account routes - minimal skeleton
  if (
    path.includes("/auth") ||
    path.includes("/forgotpassword") ||
    path.includes("/forgot-password") ||
    path.includes("/account")
  ) {
    return <PageSkeleton />;
  }

  // Preview routes
  if (path.includes("/preview") || path.includes("/folio-preview")) {
    return null;
  }

  // Legal pages
  if (path.includes("/terms") || path.includes("/privacy")) {
    return <PageSkeleton />;
  }

  // Published pages (DynamicPage) - NO skeleton at all
  // These render optimistically, showing the iframe immediately
  // Any other path that didn't match above is likely a published folio
  return null;
}

// Paths that belong to the app shell. A path counts as an app route only when
// its first segment (or its second, after a supported language prefix) is
// EXACTLY one of these names. The previous substring checks
// (currentPath.includes("/sites") etc.) also captured published-site slugs
// like "sites-demo" or "feedback-board" and broke their public URLs and
// user-testing share links.
const APP_ROUTE_SEGMENTS = new Set([
  "folio-preview", "session-review", "demo", "user-testing", "ut-test",
  "ut-results", "ut-auth", "aero-auth", "responsive-auth", "responsive",
  "iterate", "sites", "users", "account", "feedback", "login", "signup",
  // Aero login handoff: must be an app route so /redeem is never treated as
  // a user-published portfolio slug and routed to DynamicPage (blank screen).
  "redeem",
  // Aero-branded password reset request page.
  "forgot-password",
  // Firebase email action handler (/auth/action). "/auth" alone is already an
  // app route via the exact-match list above; claiming the whole first
  // segment is safe because published slugs are single-segment [a-z0-9-]
  // (see isValidReadableSlug) and can never contain a "/".
  "auth",
  // Admin subpages (/admin/ab-preview). "/admin" alone is exact-matched in the
  // list above, but its subpaths were falling through to the published-slug
  // branch and rendering blank. "admin" is a reserved label everywhere slugs
  // are made or served (slugWords RESERVED, middleware.js / api/site.js
  // RESERVED_LABELS), so no published site can ever claim this segment.
  "admin",
]);

// Aero legal pages. App routes on the Aero domain only, so Figmafolio keeps its
// existing behaviour for these paths. Without this, /privacy and /terms fell
// through to the published-site lookup and rendered a blank page. All four
// names are in slugWords RESERVED, so no published site can claim them.
const AERO_ONLY_APP_ROUTE_SEGMENTS = new Set([
  "privacy", "terms", "privacy-policy", "terms-and-conditions",
]);

// Mirrors supportedLngs in i18n.js; the routes above are declared /:lang?/<name>.
const APP_ROUTE_LANGS = new Set(["en", "es", "de", "zh", "jp", "fr"]);

function isAppRoutePath(path) {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return false;
  const first =
    APP_ROUTE_LANGS.has(segments[0]) && segments.length > 1
      ? segments[1]
      : segments[0];
  if (isAeroDomain() && AERO_ONLY_APP_ROUTE_SEGMENTS.has(first)) return true;
  return APP_ROUTE_SEGMENTS.has(first);
}

// Entry point for the legacy /auth route. MainAuth is hardcoded Figmafolio
// branding, so on the Aero domain (aeroapp.ai) we must not render it; redirect
// to the Aero-branded /login page instead, preserving any query string (e.g. a
// ?redirect= param). On figmafolio.com /auth renders MainAuth exactly as before.
function AuthEntry() {
  const location = useLocation();
  if (isAeroDomain()) {
    return <Navigate to={`/login${location.search}`} replace />;
  }
  return <Mainauth />;
}

function App() {
  const dbFirestore = firebase.firestore();
  const [data, setData] = useState([]);
  const [isMainDomain, setIsMainDomain] = useState("false");
  const [isDynamicPage, setIsDynamicPage] = useState("false");

  // useEffect(() => {
  //   logEvent(analytics, 'page_view', {
  //     page_location: location.pathname,
  //     page_path: window.location.pathname,
  //   });
  // }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      var domain = window.location.host;
      var currentPath = window.location.pathname;
      var currentLanguage = i18n.language;

      if (
        domain === "figmafolio.com" ||
        domain === "app.figmafolio.com" ||
        domain === "figma-app-tau.vercel.app" ||
        domain === "localhost:3000" ||
        domain === "aeroapp.ai" ||
        domain === "app.aeroapp.ai"
      ) {
        setIsMainDomain("true");
        if (
          currentPath === "/" ||
          currentPath === "/form" ||
          currentPath === "/admin" ||
          currentPath === "/billing" ||
          currentPath === "/dashboard" ||
          currentPath === "/preview" ||
          currentPath === "/auth" ||
          currentPath === "/forgotpassword" ||
          currentPath === "/profile" ||
          currentPath === "/pricing-test" ||
          currentPath === "/" + currentLanguage ||
          currentPath === "/" + currentLanguage + "/form" ||
          currentPath === "/" + currentLanguage + "/admin" ||
          currentPath === "/" + currentLanguage + "/billing" ||
          currentPath === "/" + currentLanguage + "/dashboard" ||
          currentPath === "/" + currentLanguage + "/preview" ||
          currentPath === "/" + currentLanguage + "/auth" ||
          currentPath === "/" + currentLanguage + "/plugin-auth" ||
          currentPath === "/" + currentLanguage + "/forgotpassword" ||
          currentPath === "/" + currentLanguage + "/profile" ||
          currentPath === "/" + currentLanguage + "/testpage" ||
          currentPath === "/" + currentLanguage + "/pricing-test"
        ) {
          setIsDynamicPage("false");
        } else if (isAppRoutePath(currentPath) || currentPath === "/aero") {
          setIsDynamicPage("false");
        } else {
          setIsDynamicPage("true");
          try {
            var generatedUrl = currentPath.slice(1);
            dbFirestore
              .collectionGroup("url")
              .where("generatedUrl", "==", generatedUrl)
              .get()
              .then((snapshot) => {
                // A creation-draft is never publicly live: drop it so no
                // DynamicPage route is created for it (figma drafts serve
                // through this SPA path; html drafts are blocked in api/site.js).
                const fetchedData = snapshot.docs
                  .map((doc) => doc.data())
                  .filter((d) => d.draft !== true);
                setData(fetchedData);
              });
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        }
      } else {
        // setIsMainDomain("false");
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Suspense fallback={<SuspenseFallback />}>
        {isMainDomain === "false" ? (
          <Routes>
            <Route path="/" element={<DynamicPage2 />} />
          </Routes>
        ) : isDynamicPage === "true" ? (
          <Routes>
            {data.map((item) => (
              <Route
                key={item.generatedUrl}
                path={`/${item.generatedUrl}`}
                element={<DynamicPage url={item} />}
              />
            ))}
          </Routes>
        ) : (
          <Routes>
            {!isAeroDomain() && <Route path="/:lang?/" element={<LandingPage />} />}
            <Route path="/admin" element={<AdminDashboard />} />
            {/* Internal, counter-neutral preview of the Aero onboarding modal
                (no analytics / cohort / seen-flag). Not user-facing. */}
            <Route path="/admin/ab-preview" element={<AbPreview />} />
            {/* Aero-side login handoff: consumes a one-time code and signs the
                user in already-authenticated (see RedeemHandoff). Origin-agnostic
                so it works on the Aero app domain that performs the redeem. */}
            <Route path="/redeem" element={<RedeemHandoff />} />
            <Route path="/:lang?/billing" element={<BillingPage />} />
            {/* On the Aero domain the Figmafolio legal URLs go to Aero's own
                pages. Figmafolio renders its pages exactly as before. */}
            <Route
              path="/:lang?/terms-and-conditions"
              element={
                isAeroDomain() ? <Navigate to="/terms" replace /> : <TermsAndConditions />
              }
            />
            <Route
              path="/:lang?/privacy-policy"
              element={
                isAeroDomain() ? <Navigate to="/privacy" replace /> : <PrivacyPolicy />
              }
            />
            {/* Aero legal pages. Public: outside AeroLayout, which shows the
                sign-in screen to logged-out visitors. */}
            <Route path="/:lang?/privacy" element={<AeroPrivacyPolicy />} />
            <Route path="/:lang?/terms" element={<AeroTermsOfService />} />
            <Route path="/:lang?/folio-form" element={<FolioForm />} />
            <Route path="/:lang?/dashboard" element={<UserDashboard />} />
            <Route path="/:lang?/testpage" element={<TestHtml />} />
            <Route path="/:lang?/pricing-test" element={<PricingTest />} />
            <Route path="/:lang?/preview" element={<Preview />} />
            <Route
              path="/:lang?/folio-preview/:id"
              element={<PreviewFromPlugin />}
            />
            <Route path="/:lang?/auth" element={<AuthEntry />} />
            {/* Firebase email action links (mode=resetPassword&oobCode=...).
                Brand-aware: Figmafolio shell on figmafolio.com, Aero card on
                aeroapp.ai, with the link's continueUrl taking precedence. */}
            <Route path="/:lang?/auth/action" element={<AuthActionPage />} />
            <Route path="/:lang?/plugin-auth" element={<PluginAuth />} />
            <Route path="/:lang?/ut-auth" element={<UTPluginAuth />} />
            <Route path="/:lang?/aero-auth" element={<UTPluginAuth />} />
            {/* Friendly Aero auth aliases — same standalone UTPluginAuth page as
                /aero-auth, differing only in which tab opens first (see the
                path-derived initial mode in UTPluginAuth). */}
            <Route path="/:lang?/login" element={<UTPluginAuth />} />
            <Route path="/:lang?/signup" element={<UTPluginAuth />} />
            <Route path="/:lang?/responsive-auth" element={<ResponsiveAuth />} />
            {/*
              All three Aero products share a single AeroLayout.
              AeroLayout renders ONCE (sidebar + auth + subscription) and stays
              mounted as the user navigates between User Testing, Responsive,
              and Iterate. Only the <Outlet> content (the product page) swaps.
              This eliminates the skeleton flash and full re-render on nav clicks.
            */}
            <Route element={<AeroLayout />}>
              {/* Aero root: on aeroapp.ai, "/" is Home (v3 layout A: what's new,
                  then sites and tests). The Tests list stays at /user-testing. */}
              {isAeroDomain() && (
                <Route path="/" element={<UserTestingLayout />}>
                  <Route index element={<AeroHome />} />
                </Route>
              )}
              {/* User Testing — has its own sub-routes (project detail, sessions, etc.) */}
              <Route path="/user-testing" element={<UserTestingLayout />}>
                <Route index element={<UserTestingDashboard />} />
                {/* New test is a page (v3); a static segment, so it wins over :projectId */}
                <Route path="new" element={<AeroTestNew />} />
                <Route path=":projectId" element={<UserTestingProject />}>
                  <Route index element={<UTProjectIndexRedirect />} />
                  <Route path="setup" element={<UTSetupTab />} />
                  <Route path="share" element={<Navigate to="../setup" replace />} />
                  <Route path="sessions" element={<UTSessionsTab />} />
                  <Route path="sessions/:sessionId" element={<UTSessionDetailPage />} />
                  <Route path="insights" element={<UTInsightsTab />} />
                </Route>
                <Route path="*" element={<AeroNotFoundPage />} />
              </Route>
              {/* Responsive and Iterate — content-only pages */}
              <Route path="/:lang?/responsive" element={<ResponsiveDashboard />} />
              <Route path="/:lang?/iterate" element={<IterateDashboard />} />
              {/* Sites — HTML site hosting. Master-detail like User Testing:
                  the list at /sites, a per-site page at /sites/:siteId. Kept as
                  flat siblings (not nested) so each receives AeroLayout's outlet
                  context directly. */}
              <Route path="/:lang?/sites" element={<AeroSites />} />
              {/* Admin-only in-app user management (Aero only). The page itself
                  reads isAdmin from AeroLayout's outlet context and redirects
                  non-admins; the sidebar only surfaces the link for admins. */}
              <Route path="/:lang?/users" element={<AeroUsers />} />
              {/* Admin-only read-only session review of another user's test (new review UI) */}
              <Route path="/:lang?/users/:ownerUid/tests/:projectId/sessions/:sessionId" element={<AdminSessionReview />} />
              {/* New site is a page (v3, phase 3b); a static segment, so it wins over :siteId */}
              <Route path="/:lang?/sites/new" element={<AeroSiteNew />} />
              <Route path="/:lang?/sites/:siteId" element={<AeroSiteDetail />} />
              <Route path="/:lang?/sites/:siteId/:tab" element={<AeroSiteDetail />} />
              <Route path="/:lang?/sites/:siteId/versions/:versionId" element={<AeroSiteVersion />} />
              {/* any other address under /sites: the in-app 404, not a blank page */}
              <Route path="/sites/*" element={<AeroNotFoundPage />} />
              {/* Account lives inside AeroLayout so the sidebar stays mounted
                  and nav between /account and other Aero pages is instant. */}
              <Route path="/:lang?/account" element={<Account />} />
            </Route>
            <Route path="/:lang?/forgotpassword" element={<ForgotPassword />} />
            {/* Aero-branded reset request page, linked from the Aero sign-in
                surfaces (UTPluginAuth and the inline AeroAuth). */}
            <Route path="/:lang?/forgot-password" element={<AeroForgotPassword />} />
            <Route path="/:lang?/profile" element={<ProfilePage />} />
            <Route
              path="/:lang?/mobile-instruction"
              element={<MobileFormInstruction />}
            />
            <Route
              path="/:lang?/mobile-form-title"
              element={<MobileFormTitle />}
            />
            <Route
              path="/:lang?/mobile-form-content"
              element={<MobileFormContent />}
            />
            <Route
              path="/:lang?/mobile-form-domain"
              element={<MobileFormDomain />}
            />
            <Route
              path="/:lang?/mobile-form-password"
              element={<MobileFormPassword />}
            />
            <Route
              path="/:lang?/mobile-form-label"
              element={<MobileFormLabel />}
            />
            <Route
              path="/:lang?/mobile-form-favicon"
              element={<MobileFormFavicon />}
            />
            <Route
              path="/:lang?/mobile-analytics"
              element={<MobileAnalytics />}
            />
            <Route
              path="/:lang?/mobile-user-testing"
              element={<MobileUserTesting />}
            />
            <Route
              path="/:lang?/session-review/:ownerId/:projectId/:sessionId"
              element={<SessionVideoPlayer />}
            />
            {/* Aero Component Showcase */}
            <Route path="/aero" element={<AeroShowcase />} />
            {/* User Testing Demo - Public route */}
            <Route path="/demo/user-testing" element={<DemoUserTesting />} />
            {/* User Testing Preview - No auth required (for Figma plugin) */}
            <Route path="/:lang?/ut-test/:testId" element={<UTPreviewTest />} />
            <Route path="/:lang?/ut-results/:testId" element={<UTPreviewResults />} />
            <Route path="/:lang?/ut-results/:testId/session/:sessionId/review" element={<PreviewSessionVideoPlayer />} />
          </Routes>
        )}
      </Suspense>
    </div>
  );
}

export default App;
